import { DEFAULT_EXAMPLES, Example, SLIDES, SUBPARTS, TAGS, Team, coverage } from './slides';
import { needsConsultDate, trust } from './sources';

export type Level = 'error' | 'warn';
export interface Item { slide: number; label: string; level: Level }
export interface Section { id: string; title: string; ok: string; items: Item[] }

interface Zone { key: string; label: string; max: number; src?: boolean }

const ZONES: Record<string, Zone[]> = {
  s1: [{ key: 'title', label: 'Titre de la présentation', max: 90 }, { key: 'date', label: "Date de l'oral", max: 40 }],
  s2: [{ key: 'title', label: 'Titre de l’intro', max: 80 }, { key: 'body', label: 'Texte de l’intro', max: 600, src: true }],
  s3: [{ key: 'problem', label: 'Problématique', max: 220, src: true }],
  s4: [{ key: 'title', label: "Titre de l'axe 1", max: 90 }],
  s8: [{ key: 'title', label: "Titre de l'axe 2", max: 90 }],
  s12: [{ key: 'answer', label: 'Réponse à la problématique', max: 480, src: true }, { key: 'open', label: 'Ouverture', max: 220 }],
};
SUBPARTS.forEach((s) => {
  ZONES[s.id] = [{ key: 'title', label: `Titre ${s.part}`, max: 90 }, { key: 'body', label: `Contenu ${s.part}`, max: 700, src: true }];
});

const where = (sid: string) => SLIDES.findIndex((s) => s.id === sid);
const txt = (v: any) => (typeof v === 'string' ? v.trim() : '');

export function runChecklist(get: (k: string) => any, team: Team): Section[] {
  const empty: Item[] = [];
  const sources: Item[] = [];
  const long: Item[] = [];

  const checkSource = (i: number, label: string, srcKey: string) => {
    const t = trust(txt(get(srcKey)));
    if (t === 'empty') sources.push({ slide: i, label: `${label} : source manquante`, level: 'error' });
    else if (t === 'check') sources.push({ slide: i, label: `${label} : site à vérifier (pas officiel ni académique)`, level: 'warn' });
    else if (t === 'ref') sources.push({ slide: i, label: `${label} : référence sans lien, à vérifier en BU`, level: 'warn' });
    if (needsConsultDate(txt(get(srcKey)))) sources.push({ slide: i, label: `${label} : date de consultation manquante (ISO 690)`, level: 'warn' });
  };

  const checkZone = (i: number, z: Zone, key: string) => {
    const v = txt(get(key));
    if (!v) { empty.push({ slide: i, label: `${z.label} est vide`, level: 'error' }); return; }
    if (v.length > z.max) long.push({ slide: i, label: `${z.label} : ${v.length}/${z.max} caractères, à raccourcir`, level: 'error' });
    else if (v.length >= z.max * 0.85) long.push({ slide: i, label: `${z.label} : ${v.length}/${z.max}, proche de la limite`, level: 'warn' });
    if (z.src) checkSource(i, z.label, `${key}:src`);
  };

  Object.entries(ZONES).forEach(([sid, zones]) => zones.forEach((z) => checkZone(where(sid), z, `${sid}:${z.key}`)));

  SUBPARTS.forEach((s) => {
    const list: Example[] = get(`${s.id}:examples`) || DEFAULT_EXAMPLES;
    list.forEach((e) => {
      const z = { key: '', label: `${s.part} · exemple ${e.name || 'sans nom'}`, max: 180, src: true };
      const key = `${s.id}:ex:${e.id}`;
      if (!txt(get(key))) empty.push({ slide: where(s.id), label: `${z.label} est vide (remplissez-le ou supprimez-le)`, level: 'error' });
      else checkZone(where(s.id), z, key);
    });
  });

  SLIDES.forEach((s, i) => { if (get(`${s.id}:img`)) checkSource(i, `Image slide ${s.n}`, `${s.id}:img:src`); });

  const cov = coverage(get);
  const prob: Item[] = TAGS.filter((t) => !cov[t.id]).map((t) => ({ slide: where('s5'), label: `Aucune sous-partie n'aborde le volet ${t.label.toLowerCase()}`, level: 'error' as Level }));
  SUBPARTS.forEach((s) => { if (!(get(`${s.id}:tags`) || []).length) prob.push({ slide: where(s.id), label: `${s.part} n'a pas d'étiquette travail / juridique / financier`, level: 'warn' }); });

  const group: Item[] = [0, 1, 2].filter((i) => !team.names[i]?.trim()).map((i) => ({ slide: 0, label: `Prénom de l'élève ${i + 1} non renseigné`, level: 'warn' as Level }));

  const bySlide = (a: Item, b: Item) => a.slide - b.slide;
  [empty, sources, long, prob].forEach((l) => l.sort(bySlide));

  return [
    { id: 'empty', title: 'Zones vides', ok: 'Toutes les zones sont remplies.', items: empty },
    { id: 'sources', title: 'Sources', ok: 'Chaque extrait a une source officielle ou académique.', items: sources },
    { id: 'problem', title: 'Couverture de la problématique', ok: 'Les volets travail, juridique et financier sont tous abordés.', items: prob },
    { id: 'long', title: 'Textes trop longs', ok: 'Aucun texte ne dépasse son cadre.', items: long },
    { id: 'group', title: 'Groupe', ok: 'Les trois prénoms sont renseignés.', items: group },
  ];
}

export const countErrors = (sections: Section[]) => sections.reduce((n, s) => n + s.items.filter((i) => i.level === 'error').length, 0);
